import { useEffect, useMemo, useRef } from "react";
import type { StageState } from "@otto/lehrgang";
import own from "./ExerciseLine.module.css";

/**
 * The exercise text as character tiles: typed ones green, the current one
 * underlined in the finger colour.
 *
 * A miss shakes the line. The shake restarts by removing and re-adding the class
 * with a forced reflow — no timer to clean up.
 */
export function ExerciseLine({
  state,
  missCount,
  shaking,
  onShakeEnd,
}: {
  state: StageState;
  missCount: number;
  shaking: boolean;
  onShakeEnd: () => void;
}) {
  const lineRef = useRef<HTMLDivElement>(null);
  const chars = useMemo(() => [...state.text], [state.text]);

  useEffect(() => {
    const node = lineRef.current;
    if (!node || missCount === 0) return;
    node.classList.remove("shake");
    void node.offsetWidth; // forces a reflow
    node.classList.add("shake");
  }, [missCount]);

  return (
    <div className={own.row} ref={lineRef} onAnimationEnd={onShakeEnd}>
      {chars.map((char, i) => (
        <span
          key={i}
          className={[
            own.char,
            i < state.pos ? own.typed : "",
            i === state.pos ? own.current : "",
            i === state.pos && shaking ? own.mistyped : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {char === " " ? "␣" : char}
        </span>
      ))}
    </div>
  );
}
